import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { Text } from '../../pulsechain_components/Text';
import { Heading } from '../../pulsechain_components/Heading';
import { Layer } from 'grommet';
import { Close } from 'grommet-icons';
import {
  WalletType,
  isWalletInstalled,
  isMobileWalletBrowser,
} from '../../connectors/walletDetection';
import { useInjectedWallet } from '../../connectors/InjectedWalletContext';
import metamaskLogo from '../../static/metamask.svg';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  margin: 20px;
  min-width: 350px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-2px);
  }
`;

const Logo = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  font-weight: bold;
`;

const ModalContent = styled.div`
  background: ${p => p.theme.gray.dark};
  border-radius: 12px;
  padding: 30px;
  max-width: 500px;
  width: 90vw;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.gray.light};
  transition: color 0.2s;

  &:hover {
    color: ${p => p.theme.white};
  }
`;

const WalletList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
`;

const WalletOption = styled.div<{ available?: boolean }>`
  padding: 15px;
  background: ${props =>
    props.available
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(255, 255, 255, 0.03)'};
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: ${props => (props.available ? 'pointer' : 'not-allowed')};
  opacity: ${props => (props.available ? 1 : 0.5)};
  transition: all 0.2s;
  border: 1px solid
    ${props => (props.available ? 'rgba(255, 0, 153, 0.3)' : 'transparent')};

  &:hover {
    background: ${props =>
      props.available
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(255, 255, 255, 0.03)'};
    transform: ${props => (props.available ? 'translateX(5px)' : 'none')};
    border-color: ${props =>
      props.available ? 'rgba(255, 0, 153, 0.5)' : 'transparent'};
  }
`;

const WalletIcon = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 6px;
`;

const ConnectButton = styled.span`
  margin-left: auto;
  padding: 6px 16px;
  background: linear-gradient(135deg, #ff0099 0%, #ff6600 100%);
  color: white;
  border-radius: 6px;
  font-size: 13px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(255, 0, 153, 0.4);
  }
`;

const ErrorMessage = styled.div`
  margin-top: 10px;
  padding: 12px 16px;
  background: rgba(220, 53, 69, 0.15);
  border: 1px solid rgba(220, 53, 69, 0.4);
  border-radius: 8px;
  color: #ff6b6b;
  font-size: 14px;
`;

interface WalletInfo {
  name: string;
  icon?: string;
  available: boolean;
  connectMethod?: () => void;
}

const MultiWalletButton: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [detectedWallets, setDetectedWallets] = useState<WalletType[]>([]);
  const [hasGenericProvider, setHasGenericProvider] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { connect } = useInjectedWallet();

  // Detect available wallets - runs on mount and when modal opens
  const detectWallets = useCallback(() => {
    const detected: WalletType[] = [];

    if (isWalletInstalled(WalletType.METAMASK)) {
      detected.push(WalletType.METAMASK);
    }
    if (isWalletInstalled(WalletType.TRUST)) {
      detected.push(WalletType.TRUST);
    }
    if (isWalletInstalled(WalletType.COINBASE)) {
      detected.push(WalletType.COINBASE);
    }
    if (isWalletInstalled(WalletType.TOKENPOCKET)) {
      detected.push(WalletType.TOKENPOCKET);
    }
    if (isWalletInstalled(WalletType.BRAVE)) {
      detected.push(WalletType.BRAVE);
    }
    if (isWalletInstalled(WalletType.PHANTOM)) {
      detected.push(WalletType.PHANTOM);
    }
    if (isWalletInstalled(WalletType.RABBY)) {
      detected.push(WalletType.RABBY);
    }

    // Check if we're in a mobile wallet browser
    const { isWalletBrowser, type } = isMobileWalletBrowser();
    if (isWalletBrowser && !detected.includes(type)) {
      detected.push(type);
    }

    // Check for generic ethereum provider (imToken, other wallets)
    // If window.ethereum exists but no specific wallet detected, allow generic connection
    const hasEthereum =
      typeof window !== 'undefined' && !!(window as any).ethereum;
    setHasGenericProvider(hasEthereum && detected.length === 0);

    setDetectedWallets(detected);
  }, []);

  useEffect(() => {
    detectWallets();
  }, [detectWallets]);

  // Re-detect wallets and clear error when modal opens
  useEffect(() => {
    if (showModal) {
      detectWallets();
      setError(null);
    }
  }, [showModal, detectWallets]);

  const handleWalletConnect = async (walletType: WalletType) => {
    setConnecting(true);
    setError(null);

    try {
      // Use the injected wallet connector directly
      // This avoids the postMessage cloning error (GitLab issue #4)
      // that occurs when using the MetaMask connector with other wallets
      await connect(walletType);

      setShowModal(false);
    } catch (connectionError) {
      // User-friendly error messages
      const err = connectionError as { code?: number; message?: string };
      if (err.code === 4001 || err.message?.includes('User rejected')) {
        setError('Connection request was rejected. Please try again.');
      } else if (err.code === -32002) {
        setError(
          'A connection request is already pending. Please check your wallet.'
        );
      } else {
        setError(
          `Failed to connect to ${walletType}. ${err.message ||
            'Please try again.'}`
        );
      }
    } finally {
      setConnecting(false);
    }
  };

  const walletOptions: WalletInfo[] = [
    {
      name: 'MetaMask',
      icon: metamaskLogo,
      available: detectedWallets.includes(WalletType.METAMASK),
      connectMethod: () => handleWalletConnect(WalletType.METAMASK),
    },
    {
      name: 'Rabby',
      available: detectedWallets.includes(WalletType.RABBY),
      connectMethod: () => handleWalletConnect(WalletType.RABBY),
    },
    {
      name: 'Trust Wallet',
      icon:
        'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE2IDJDMjMuNzMyIDIgMzAgOC4yNjggMzAgMTZDMzAgMjMuNzMyIDIzLjczMiAzMCAxNiAzMEM4LjI2OCAzMCAyIDIzLjczMiAyIDE2QzIgOC4yNjggOC4yNjggMiAxNiAyWiIgZmlsbD0iIzMzNzVCQiIvPgo8cGF0aCBkPSJNMTYgNy41QzE2LjggNy41IDE3LjUgOCAxNy41IDkuNVYxMy41QzE4LjUgMTMuNSAxOS41IDE0LjUgMTkuNSAxNS41VjIwLjVDMTkuNSAyMS41IDE4LjUgMjIuNSAxNy41IDIyLjVIMTQuNUMxMy41IDIyLjUgMTIuNSAyMS41IDEyLjUgMjAuNVYxNS41QzEyLjUgMTQuNSAxMy41IDEzLjUgMTQuNSAxMy41VjkuNUMxNC41IDggMTUuMiA3LjUgMTYgNy41WiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+',
      available: detectedWallets.includes(WalletType.TRUST),
      connectMethod: () => handleWalletConnect(WalletType.TRUST),
    },
    {
      name: 'Brave Wallet',
      available: detectedWallets.includes(WalletType.BRAVE),
      connectMethod: () => handleWalletConnect(WalletType.BRAVE),
    },
    {
      name: 'Phantom',
      available: detectedWallets.includes(WalletType.PHANTOM),
      connectMethod: () => handleWalletConnect(WalletType.PHANTOM),
    },
    {
      name: 'Coinbase Wallet',
      available: detectedWallets.includes(WalletType.COINBASE),
      connectMethod: () => handleWalletConnect(WalletType.COINBASE),
    },
    {
      name: 'TokenPocket',
      available: detectedWallets.includes(WalletType.TOKENPOCKET),
      connectMethod: () => handleWalletConnect(WalletType.TOKENPOCKET),
    },
    {
      name: 'Browser Wallet',
      available: hasGenericProvider || detectedWallets.length > 0,
      connectMethod: () => handleWalletConnect(WalletType.UNKNOWN),
    },
  ];

  return (
    <>
      <Container onClick={() => setShowModal(true)}>
        <Logo>W</Logo>
        <Text size="large" weight="bold">
          <FormattedMessage defaultMessage="Connect More Wallets" />
        </Text>
        <Text size="small" color="grayLight">
          {detectedWallets.length > 0 ? (
            <FormattedMessage
              defaultMessage="{count} wallet(s) detected"
              values={{ count: detectedWallets.length }}
            />
          ) : (
            <FormattedMessage defaultMessage="WalletConnect Protocol" />
          )}
        </Text>
        {detectedWallets.includes(WalletType.RABBY) && (
          <Text size="xSmall" color="greenDark" style={{ fontWeight: 'bold' }}>
            <FormattedMessage defaultMessage="✓ Rabby Available" />
          </Text>
        )}
        {detectedWallets.includes(WalletType.TRUST) && (
          <Text size="xSmall" color="greenDark" style={{ fontWeight: 'bold' }}>
            <FormattedMessage defaultMessage="✓ Trust Wallet Available" />
          </Text>
        )}
      </Container>

      {showModal && (
        <Layer
          onEsc={() => setShowModal(false)}
          onClickOutside={() => setShowModal(false)}
        >
          <ModalContent>
            <ModalHeader>
              <Heading level={2} size="medium">
                <FormattedMessage defaultMessage="Select a Wallet" />
              </Heading>
              <CloseButton onClick={() => setShowModal(false)}>
                <Close />
              </CloseButton>
            </ModalHeader>

            {connecting && (
              <Text
                size="small"
                color="blueMedium"
                style={{ marginTop: '10px' }}
              >
                <FormattedMessage defaultMessage="Connecting..." />
              </Text>
            )}

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <WalletList>
              {walletOptions
                .filter(wallet => wallet.available)
                .map(wallet => (
                  <WalletOption
                    key={wallet.name}
                    available={wallet.available}
                    onClick={() => {
                      if (wallet.connectMethod) {
                        wallet.connectMethod();
                      }
                    }}
                  >
                    {wallet.icon ? (
                      <WalletIcon src={wallet.icon} alt={wallet.name} />
                    ) : (
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          background:
                            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: 6,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      >
                        {wallet.name.charAt(0)}
                      </div>
                    )}
                    <Text size="medium" weight="bold">
                      {wallet.name}
                    </Text>
                    <ConnectButton
                      onClick={e => {
                        e.stopPropagation();
                        if (wallet.connectMethod) {
                          wallet.connectMethod();
                        }
                      }}
                    >
                      Connect
                    </ConnectButton>
                  </WalletOption>
                ))}
            </WalletList>

            <div
              style={{
                marginTop: '30px',
                padding: '20px',
                background: 'rgba(255, 0, 153, 0.1)',
                borderRadius: '8px',
              }}
            >
              <Text size="small">
                <FormattedMessage defaultMessage="Note: WalletConnect v2 integration is currently being developed. Once complete, you'll be able to connect using any WalletConnect-compatible wallet by scanning a QR code or using deep linking." />
              </Text>
            </div>
          </ModalContent>
        </Layer>
      )}
    </>
  );
};

export default MultiWalletButton;
